import React from 'react';
import PropTypes from 'prop-types';
import { Card, Table } from 'react-bootstrap';
import {
  calculateDailySicScore,
  calculateSectorSicScore,
  calculateWeeklyAverage,
  formatSicPercent,
  isSicScorePassing
} from 'helpers/sicHelper';
import { resultColors } from './sicTableStyles';

const isWeekendDate = date => date.day() === 0 || date.day() === 6;

export default function SicSummaryCard({ sectors, weekDates, styles }) {
  const dailySicScores = weekDates.map((_, dayIndex) =>
    calculateDailySicScore(sectors, dayIndex)
  );
  const weeklySicScore = calculateWeeklyAverage(dailySicScores);

  const scoreCellStyle = (baseStyle, score) => ({
    ...baseStyle,
    textAlign: 'center',
    ...resultColors(styles, score === null ? null : isSicScorePassing(score))
  });

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Short Interval Control Score</h5>
        <div
          style={{
            ...scoreCellStyle({}, weeklySicScore),
            borderRadius: '6px',
            padding: '6px 16px',
            fontWeight: 'bold'
          }}
        >
          Weekly SIC: {formatSicPercent(weeklySicScore)}
        </div>
      </Card.Header>
      <Card.Body className="pt-0">
        <Table responsive style={{ borderCollapse: 'collapse', marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ ...styles.headerCell, textAlign: 'left', minWidth: '220px' }}>
                Sector
              </th>
              {weekDates.map(date => (
                <th
                  key={date.format('YYYY-MM-DD')}
                  style={{
                    ...styles.headerCell,
                    ...(isWeekendDate(date) ? styles.weekendHeaderCell : {})
                  }}
                >
                  <div>{date.format('ddd')}</div>
                  <div style={{ fontWeight: 'normal' }}>{date.format('DD-MMM')}</div>
                </th>
              ))}
              <th style={styles.headerCell}>Week</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...styles.sicRowCell, textAlign: 'left' }}>Daily SIC Score</td>
              {dailySicScores.map((score, dayIndex) => (
                <td key={dayIndex} style={scoreCellStyle(styles.sicRowCell, score)}>
                  {formatSicPercent(score)}
                </td>
              ))}
              <td style={scoreCellStyle(styles.sicRowCell, weeklySicScore)}>
                {formatSicPercent(weeklySicScore)}
              </td>
            </tr>
            {sectors.map((sector, rowIndex) => {
              const sectorScores = weekDates.map((_, dayIndex) =>
                calculateSectorSicScore(sector.measurables, dayIndex)
              );
              const sectorWeeklyScore = calculateWeeklyAverage(sectorScores);
              const rowCell = {
                ...styles.bodyCell,
                backgroundColor: styles.zebraBackground(rowIndex)
              };
              return (
                <tr key={sector.id}>
                  <td style={rowCell}>{sector.name}</td>
                  {sectorScores.map((score, dayIndex) => (
                    <td key={dayIndex} style={scoreCellStyle(rowCell, score)}>
                      {formatSicPercent(score)}
                    </td>
                  ))}
                  <td style={scoreCellStyle({ ...rowCell, fontWeight: 600 }, sectorWeeklyScore)}>
                    {formatSicPercent(sectorWeeklyScore)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

SicSummaryCard.propTypes = {
  sectors: PropTypes.array.isRequired,
  weekDates: PropTypes.array.isRequired,
  styles: PropTypes.object.isRequired
};
